import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { dataHora, rotulo } from '../formato';
import {
  Area, Carregando, Dialogo, ErroDaApi, FotoProtegida, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';

/**
 * Triagem dos relatos do beta.
 *
 * A tela é ordenada por **aberto primeiro** e mostra, em cada linha
 * aberta, há quantos dias ela espera. É o número que denuncia o canal
 * abandonado — e canal de beta abandonado é pior que canal nenhum,
 * porque consumiu a boa vontade de quem relatou e não devolveu nada.
 *
 * Fechar exige resposta escrita. Isso não é gentileza: um relato que
 * vira "não procede" em silêncio ensina o testador a parar de relatar, e
 * o que se perde não é aquele relato, são os próximos vinte. A regra
 * está no banco (`desfecho_exige_resposta`), não só aqui.
 */

interface Relato {
  id: string;
  tipo: string;
  titulo: string;
  descricao: string;
  situacao: string;
  aberto: boolean;
  dias_esperando: number | null;
  relatado_em: string;
  autor_nome: string | null;
  autor_email: string;
  rota: string | null;
  url: string | null;
  navegador: string | null;
  viewport: string | null;
  versao: string | null;
  arquivo_id: string | null;
  resposta: string | null;
  respondido_por_nome: string | null;
  respondido_em: string | null;
}

const TOM: Record<string, Tom> = {
  novo: 'atencao',
  'em analise': 'andamento',
  aceito: 'andamento',
  resolvido: 'ok',
  'nao procede': 'neutro',
  duplicado: 'neutro',
};

const ROTULO_TIPO: Record<string, string> = {
  erro: 'Deu erro',
  fluxo: 'Fluxo estranho',
  tela: 'Tela / layout',
  texto: 'Texto errado',
  lentidao: 'Lentidão',
  sugestao: 'Sugestão',
};

const SITUACOES = ['novo', 'em analise', 'aceito', 'resolvido',
  'nao procede', 'duplicado'] as const;

export function Relatos() {
  const [linhas, setLinhas] = useState<Relato[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [filtro, setFiltro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [alvo, setAlvo] = useState<Relato | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (filtro) q.set('situacao', filtro);
      if (busca) q.set('busca', busca);
      setLinhas(await api<Relato[]>(`/relatos?${q}`));
    } catch (e) { setErro(e); }
  }, [filtro, busca]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!linhas) return <Carregando />;

  const abertos = linhas.filter((r) => r.aberto).length;

  return (
    <div>
      <div className="cabeca">
        <h1>Relatos do beta</h1>
        <p className="sub">
          O que os testadores encontraram — com a tela, a hora e a versão junto
        </p>
      </div>

      <div className="linha" style={{ marginBottom: '.9rem' }}>
        <input
          type="search"
          placeholder="Buscar no título ou na descrição"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          aria-label="Buscar relato"
        />
      </div>

      <div className="filtros">
        <button type="button"
          className={`botao ${filtro === null ? '' : 'discreto'}`}
          onClick={() => setFiltro(null)}>Todos</button>
        {SITUACOES.map((s) => (
          <button key={s} type="button"
            className={`botao ${filtro === s ? '' : 'discreto'}`}
            onClick={() => setFiltro(filtro === s ? null : s)}>{rotulo(s)}</button>
        ))}
      </div>

      <div className="cartao">
        <div className="cartao-cabeca">
          <h2>Relatos</h2>
          <span className="sub">
            {linhas.length} no filtro · <strong>{abertos}</strong> em aberto
          </span>
        </div>

        {linhas.length === 0 ? (
          <Vazio>Nenhum relato aqui.</Vazio>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Relato</th>
                  <th scope="col" className="col-opcional">Tipo</th>
                  <th scope="col" className="col-opcional">Quem / quando</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((r) => (
                  <tr key={r.id} className={r.aberto ? undefined : 'apagada'}>
                    <th scope="row">
                      <button type="button" className="ligacao"
                        onClick={() => setAlvo(r)}>
                        <strong>{r.titulo}</strong>
                      </button>
                      <span className="sub">
                        {r.rota ?? '—'}
                        {r.arquivo_id ? ' · com print' : ''}
                      </span>
                    </th>
                    <td className="col-opcional">{ROTULO_TIPO[r.tipo] ?? r.tipo}</td>
                    <td className="col-opcional">
                      <span className="sub">{r.autor_nome ?? r.autor_email}</span>
                      <span className="sub">{dataHora(r.relatado_em)}</span>
                    </td>
                    <td>
                      <Selo tom={TOM[r.situacao] ?? 'neutro'}>{rotulo(r.situacao)}</Selo>
                      {r.aberto && r.dias_esperando !== null && r.dias_esperando >= 3 && (
                        <span className="sub" style={{ display: 'block' }}>
                          esperando há {r.dias_esperando} dia(s)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="rodape-regra">
          Relato <strong>não se apaga</strong> — “não procede” é um desfecho
          registrado, com quem decidiu e por quê. O que alguém achou confuso
          é informação do beta, mesmo quando o sistema estava certo. E
          fechar exige resposta escrita: relato fechado em silêncio ensina
          o testador a parar de relatar.
        </p>
      </div>

      {alvo && (
        <FormTriagem
          relato={alvo}
          aoFechar={() => setAlvo(null)}
          aoPronto={() => { setAlvo(null); void carregar(); }}
        />
      )}
    </div>
  );
}

function FormTriagem({ relato, aoFechar, aoPronto }: {
  relato: Relato; aoFechar: () => void; aoPronto: () => void;
}) {
  const [situacao, setSituacao] = useState(relato.situacao);
  const [resposta, setResposta] = useState(relato.resposta ?? '');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const fecha = !['novo', 'em analise'].includes(situacao);
  const invalido = fecha && resposta.trim().length < 3;

  const gravar = async () => {
    setEnviando(true); setErro(null);
    try {
      await api(`/relatos/${relato.id}`, {
        metodo: 'PATCH', corpo: { situacao, resposta },
      });
      aoPronto();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo={relato.titulo} aoFechar={aoFechar}>
      <p className="sub" style={{ marginTop: 0 }}>
        {ROTULO_TIPO[relato.tipo] ?? relato.tipo} ·{' '}
        {relato.autor_nome ?? relato.autor_email} · {dataHora(relato.relatado_em)}
      </p>

      <p className="relato-corpo">{relato.descricao}</p>

      {/*
        O contexto que a tela capturou. É o que separa um relato
        acionável de "não consegui salvar aquela tela ontem".
      */}
      <div className="relato-contexto">
        {relato.rota && <span>rota <code>{relato.rota}</code></span>}
        {relato.viewport && <span>janela {relato.viewport}</span>}
        {relato.versao && <span>versão <code>{relato.versao}</code></span>}
        {relato.navegador && <span title={relato.navegador}>navegador registrado</span>}
      </div>

      {relato.arquivo_id && (
        <div style={{ margin: '.8rem 0' }}>
          <FotoProtegida arquivoId={relato.arquivo_id} alt="Print enviado pelo testador" />
        </div>
      )}

      <label className="campo">
        <span className="rotulo">Situação</span>
        <select value={situacao} onChange={(e) => setSituacao(e.currentTarget.value)}>
          {SITUACOES.map((s) => (
            <option key={s} value={s}>{rotulo(s)}</option>
          ))}
        </select>
      </label>

      <Area
        rotulo="Resposta a quem relatou"
        dica={fecha
          ? 'Obrigatória para fechar — o banco recusa desfecho sem resposta.'
          : 'Opcional enquanto o relato está em aberto.'}
        rows={4}
        value={resposta}
        onChange={(e) => setResposta(e.currentTarget.value)}
      />

      {relato.respondido_em && (
        <p className="sub">
          Última resposta por {relato.respondido_por_nome ?? '—'} em{' '}
          {dataHora(relato.respondido_em)}.
        </p>
      )}

      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="rodape-dialogo">
        <button type="button" className="secundario" onClick={aoFechar}>Fechar</button>
        <button type="button" onClick={() => void gravar()}
          disabled={enviando || invalido}>
          {enviando ? 'Gravando…' : 'Gravar'}
        </button>
      </div>
    </Dialogo>
  );
}
